import { crewOnShip } from "./crew";
import { getNetworks, type ResourceNetwork } from "./networks";
import { moduleOperational, shipCargoKgOf, totalCrewCapacity } from "./ship";
import { moduleActiveWasteHeatW } from "./thermal";
import type { ModuleDef, Ship, ShipModule, World } from "./types";

interface LiveStatRow {
  key: string;
  label: string;
  value: string;
}

/** True iff the module is drawing its `powerActiveW` this tick. */
function moduleActiveDraw(ship: Ship, m: ShipModule, def: ModuleDef, powered: boolean): boolean {
  switch (def.category) {
    case "weapon": {
      const w = def.weapon;
      const familyMatch = ship.fire1 && powered && w != null
        && (w.altFire === true) === (ship.fireMode === "mining");
      const hasAmmo = !w || w.ammoMax == null || (m.ammo ?? 0) > 0;
      return familyMatch && hasAmmo;
    }
    case "life_support":
    case "cockpit":
      return powered;
    default: return false;
  }
}

export function liveModuleStats(world: World, ship: Ship, m: ShipModule): LiveStatRow[] {
  const def = m.def;
  const rows: LiveStatRow[] = [];

  rows.push({ key: "hp", label: "HP", value: `${formatInt(m.hp)} / ${formatInt(def.stats.hp)}` });
  rows.push({ key: "temp", label: "TEMP", value: `${formatTempC(m.tempK)} / ${formatTempC(def.stats.meltPointK)}` });

  const heat = liveModuleHeatW(ship, m);
  if (heat.max > 0) {
    rows.push({ key: "heat", label: "HEAT", value: `${formatW(heat.current)} / ${formatW(heat.max)}` });
  }

  if (def.category !== "reactor" && def.category !== "battery") {
    const power = liveModulePowerW(ship, m);
    rows.push({ key: "power", label: "POWER", value: formatPowerCurNeed(power.current, power.needed) });
  }

  if (def.stats.crew > 0) {
    const status = m.manned === false ? "UNMANNED" : "MANNED";
    rows.push({ key: "crew", label: "CREW", value: `${def.stats.crew} (${status})` });
  }
  if (def.stats.crewCapacity > 0) {
    rows.push({
      key: "housed",
      label: "HOUSED",
      value: `${crewOnShip(world, ship).length} / ${totalCrewCapacity(ship)}`,
    });
  }

  switch (def.category) {
    case "thruster":
    case "engine": {
      const thrust = liveModuleThrustN(m);
      rows.push({ key: "thrust", label: "THRUST", value: `${formatN(thrust.current)} / ${formatN(thrust.max)}` });
      break;
    }
    case "reactor": {
      const max = def.stats.powerOutW;
      const current = liveReactorOutputW(ship, m, max);
      rows.push({ key: "output", label: "OUTPUT", value: `${formatW(current)} / ${formatW(max)}` });
      if (def.reactor) {
        const buf = m.fuelBuffer ?? 0;
        rows.push({ key: "hopper", label: "HOPPER", value: `${buf.toFixed(3)} / ${def.reactor.fuelBufferKg} kg` });
        const cargoKg = shipCargoKgOf(ship, def.reactor.fuelResource);
        rows.push({
          key: "fuel",
          label: "CARGO",
          value: m.fuelStarved ? "STARVED" : `${cargoKg.toFixed(2)} kg ${def.reactor.fuelResource}`,
        });
      }
      break;
    }
    case "battery": {
      const cap = def.stats.batteryWh;
      const stored = m.energyWh ?? 0;
      rows.push({ key: "charge", label: "CHARGE", value: `${formatWh(stored)} / ${formatWh(cap)}` });
      const net = getNetworks(ship).networkOf.get(m);
      const surplus = net ? liveNetworkSurplusW(ship, net) : 0;
      rows.push({
        key: "rate",
        label: "RATE",
        value: surplus >= 0 ? `+${formatW(surplus)}` : `-${formatW(-surplus)}`,
      });
      break;
    }
    case "weapon": {
      const w = def.weapon;
      if (w && w.ammoMax != null) {
        rows.push({ key: "ammo", label: "AMMO", value: `${m.ammo ?? 0} / ${w.ammoMax}` });
        if (w.ammoResource) {
          const have = shipCargoKgOf(ship, w.ammoResource);
          rows.push({ key: "ammo_cargo", label: "CARGO", value: `${have.toFixed(2)} kg ${w.ammoResource}` });
        }
      } else if (w?.kind === "beam") {
        rows.push({ key: "ammo", label: "AMMO", value: "—" });
      }
      break;
    }
    default: break;
  }

  return rows;
}

/** Signed: + when producing, − when consuming. */
function liveModulePowerW(ship: Ship, m: ShipModule): { current: number; needed: number } {
  if (m.hp <= 0) return { current: 0, needed: 0 };
  const def = m.def;
  const s = def.stats;
  const powered = m.powered ?? false;
  const operational = moduleOperational(ship, m);
  let current = 0;
  let needed = 0;

  if (def.category === "reactor") {
    current += operational ? s.powerOutW : 0;
    needed += s.powerOutW;
  }

  if (s.powerIdleW > 0) {
    current -= operational ? s.powerIdleW : 0;
    needed -= s.powerIdleW;
  }

  if (s.powerActiveW > 0) {
    const active = moduleActiveDraw(ship, m, def, powered);
    if (active && operational) current -= s.powerActiveW;
    needed -= s.powerActiveW;
  }

  if (def.thrusters) {
    let cur = 0;
    let max = 0;
    for (let i = 0; i < def.thrusters.length; i++) {
      const port = def.thrusters[i];
      const a = m.thrusterActivations[i] ?? 0;
      cur += port.powerW * a;
      max += port.powerW;
    }
    if (operational) current -= cur;
    needed -= max;
  }

  return { current, needed };
}

function liveReactorOutputW(ship: Ship, m: ShipModule, max: number): number {
  if (!moduleOperational(ship, m)) return 0;
  if (m.loadFactor != null) return max * m.loadFactor;
  const net = getNetworks(ship).networkOf.get(m);
  if (!net) return 0;
  const cap = networkReactorCapacityW(ship, net);
  if (cap <= 0) return 0;
  const demand = networkPowerDrawW(ship, net);
  return Math.min(max, (demand * max) / cap);
}

function liveNetworkSurplusW(ship: Ship, net: ResourceNetwork): number {
  let producedW = 0;
  for (const m of net.modules) {
    if (m.hp <= 0 || m.disabled) continue;
    const def = m.def;
    if (def.category === "reactor") {
      producedW += def.stats.powerOutW * (m.loadFactor ?? 0);
    }
  }
  return producedW - networkPowerDrawW(ship, net);
}

function networkReactorCapacityW(ship: Ship, net: ResourceNetwork): number {
  let cap = 0;
  for (const m of net.modules) {
    const def = m.def;
    if (def.category !== "reactor") continue;
    if (!moduleOperational(ship, m)) continue;
    cap += def.stats.powerOutW;
  }
  return cap;
}

function networkPowerDrawW(ship: Ship, net: ResourceNetwork): number {
  let draw = 0;
  for (const m of net.modules) {
    if (m.hp <= 0) continue;
    const def = m.def;
    const s = def.stats;
    if (!moduleOperational(ship, m)) continue;
    const powered = m.powered ?? false;

    if (s.powerIdleW > 0) draw += s.powerIdleW;
    if (s.powerActiveW > 0 && moduleActiveDraw(ship, m, def, powered)) draw += s.powerActiveW;

    if (def.thrusters) {
      for (let i = 0; i < def.thrusters.length; i++) {
        draw += def.thrusters[i].powerW * (m.thrusterActivations[i] ?? 0);
      }
    }
  }
  return draw;
}

function liveModuleHeatW(ship: Ship, m: ShipModule): { current: number; max: number } {
  if (m.hp <= 0) return { current: 0, max: 0 };
  const def = m.def;
  const powered = m.powered ?? false;
  const operational = moduleOperational(ship, m);
  const maxWaste = moduleActiveWasteHeatW(def);
  let current = 0;
  let max = 0;

  if (def.category === "reactor" && def.stats.powerOutW > 0) {
    if (operational) current += maxWaste * (m.loadFactor ?? 0);
    max += maxWaste;
  } else if (maxWaste > 0) {
    const active = moduleActiveDraw(ship, m, def, powered);
    if (active && operational) current += maxWaste;
    max += maxWaste;
  }

  if (def.thrusters) {
    let cur = 0;
    let mx = 0;
    for (let i = 0; i < def.thrusters.length; i++) {
      const port = def.thrusters[i];
      const a = m.thrusterActivations[i] ?? 0;
      cur += port.heatW * a;
      mx += port.heatW;
    }
    if (operational) current += cur;
    max += mx;
  }

  return { current, max };
}

function liveModuleThrustN(m: ShipModule): { current: number; max: number } {
  const def = m.def;
  if (!def.thrusters) return { current: 0, max: 0 };
  let cur = 0;
  let max = 0;
  for (let i = 0; i < def.thrusters.length; i++) {
    const port = def.thrusters[i];
    cur += port.thrustN * (m.thrusterActivations[i] ?? 0);
    max += port.thrustN;
  }
  return { current: cur, max };
}

function formatInt(n: number): string {
  return Math.max(0, Math.round(n)).toString();
}

export function formatW(w: number): string {
  const a = Math.abs(w);
  if (a >= 1_000_000) return `${(w / 1_000_000).toFixed(1)} MW`;
  if (a >= 1_000) return `${(w / 1_000).toFixed(1)} kW`;
  return `${w.toFixed(0)} W`;
}

function formatWh(wh: number): string {
  if (Math.abs(wh) >= 1_000) return `${(wh / 1_000).toFixed(2)} kWh`;
  return `${wh.toFixed(0)} Wh`;
}

export function formatTempC(tempK: number): string {
  return `${(tempK - 273.15).toFixed(0)}°C`;
}

export function formatN(n: number): string {
  const a = Math.abs(n);
  if (a >= 1_000_000) return `${(n / 1_000_000).toFixed(2)} MN`;
  if (a >= 1_000) return `${(n / 1_000).toFixed(1)} kN`;
  return `${n.toFixed(0)} N`;
}

function formatPowerCurNeed(currentW: number, neededW: number): string {
  if (neededW > 0) return `+${formatW(currentW)} / +${formatW(neededW)}`;
  if (neededW === 0 && currentW === 0) return "—";
  return `${formatW(-currentW)} / ${formatW(-neededW)}`;
}
